/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  Document,
  PaginatedAdminDocumentList,
  PaginatedDocumentList,
  PaginatedDropdownElementList,
  PureDocumentType,
} from '../models/index';
import {
    DocumentFromJSON,
    DocumentToJSON,
    PaginatedAdminDocumentListFromJSON,
    PaginatedAdminDocumentListToJSON,
    PaginatedDocumentListFromJSON,
    PaginatedDocumentListToJSON,
    PaginatedDropdownElementListFromJSON,
    PaginatedDropdownElementListToJSON,
    PureDocumentTypeFromJSON,
    PureDocumentTypeToJSON,
} from '../models/index';

export interface AdminDocumentsCreateRequest {
    file: Blob;
    documentType?: PureDocumentType;
}

export interface AdminDocumentsDropdownListRequest {
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    field?: AdminDocumentsDropdownListFieldEnum;
    id?: string;
    investment?: string;
    limit?: number;
    name?: string;
    offset?: number;
    ordering?: AdminDocumentsDropdownListOrderingEnum;
}

export interface AdminDocumentsListRequest {
    investment: string;
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    id?: Array<string>;
    limit?: number;
    name?: Array<string>;
    offset?: number;
    ordering?: AdminDocumentsListOrderingEnum;
}

export interface AdminDocumentsValuesRetrieveRequest {
    field: AdminDocumentsValuesRetrieveFieldEnum;
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    icontains?: string;
    id?: string;
    investment?: string;
    name?: string;
    ordering?: AdminDocumentsValuesRetrieveOrderingEnum;
}

export interface DocumentsCreateRequest {
    file: Blob;
    documentType?: PureDocumentType;
}

export interface DocumentsDropdownListRequest {
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    field?: DocumentsDropdownListFieldEnum;
    id?: string;
    investmentId?: string;
    limit?: number;
    name?: string;
    offset?: number;
    ordering?: DocumentsDropdownListOrderingEnum;
}

export interface DocumentsListRequest {
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    id?: Array<string>;
    investmentId?: Array<string>;
    limit?: number;
    name?: Array<string>;
    offset?: number;
    ordering?: DocumentsListOrderingEnum;
}

export interface DocumentsValuesRetrieveRequest {
    field: DocumentsValuesRetrieveFieldEnum;
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    icontains?: string;
    id?: string;
    investmentId?: string;
    name?: string;
    ordering?: DocumentsValuesRetrieveOrderingEnum;
}

/**
 * 
 */
export class DocumentsApi extends runtime.BaseAPI {

    /**
     * Can be used to upload documents for use in investor import. Files are uploaded via HTTP multipart, see the [documentation](https://swagger.io/docs/specification/describing-request-body/file-upload/#multipart).
     * Create
     */
    async adminDocumentsCreateRaw(requestParameters: AdminDocumentsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Document>> {
        if (requestParameters['file'] == null) {
            throw new runtime.RequiredError(
                'file',
                'Required parameter "file" was null or undefined when calling adminDocumentsCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const consumes: runtime.Consume[] = [
            { contentType: 'multipart/form-data' },
        ];
        // @ts-ignore: canConsumeForm may be unused
        const canConsumeForm = runtime.canConsumeForm(consumes);

        let formParams: { append(param: string, value: any): any };
        let useForm = false;
        // use FormData to transmit files using content-type "multipart/form-data"
        useForm = canConsumeForm;
        if (useForm) {
            formParams = new FormData();
        } else {
            formParams = new URLSearchParams();
        }

        if (requestParameters['documentType'] != null) {
            formParams.append('document_type', requestParameters['documentType'] as any);
        }

        if (requestParameters['file'] != null) {
            formParams.append('file', requestParameters['file'] as any);
        }

        const response = await this.request({
            path: `/api/admin/documents/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: formParams,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => DocumentFromJSON(jsonValue));
    }

    /**
     * Can be used to upload documents for use in investor import. Files are uploaded via HTTP multipart, see the [documentation](https://swagger.io/docs/specification/describing-request-body/file-upload/#multipart).
     * Create
     */
    async adminDocumentsCreate(requestParameters: AdminDocumentsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Document> {
        const response = await this.adminDocumentsCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async adminDocumentsDropdownListRaw(requestParameters: AdminDocumentsDropdownListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedDropdownElementList>> {
        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investment'] != null) {
            queryParameters['investment'] = requestParameters['investment'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['name'] != null) {
            queryParameters['name'] = requestParameters['name'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/admin/documents/dropdown/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedDropdownElementListFromJSON(jsonValue));
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async adminDocumentsDropdownList(requestParameters: AdminDocumentsDropdownListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedDropdownElementList> {
        const response = await this.adminDocumentsDropdownListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * List documents with optional filtering by investment ID.
     * List documents
     */
    async adminDocumentsListRaw(requestParameters: AdminDocumentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedAdminDocumentList>> {
        if (requestParameters['investment'] == null) {
            throw new runtime.RequiredError(
                'investment',
                'Required parameter "investment" was null or undefined when calling adminDocumentsList().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investment'] != null) {
            queryParameters['investment'] = requestParameters['investment'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['name'] != null) {
            queryParameters['name'] = requestParameters['name'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/admin/documents/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedAdminDocumentListFromJSON(jsonValue));
    }

    /**
     * List documents with optional filtering by investment ID.
     * List documents
     */
    async adminDocumentsList(requestParameters: AdminDocumentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedAdminDocumentList> {
        const response = await this.adminDocumentsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async adminDocumentsValuesRetrieveRaw(requestParameters: AdminDocumentsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<string>>> {
        if (requestParameters['field'] == null) {
            throw new runtime.RequiredError(
                'field',
                'Required parameter "field" was null or undefined when calling adminDocumentsValuesRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['icontains'] != null) {
            queryParameters['icontains'] = requestParameters['icontains'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investment'] != null) {
            queryParameters['investment'] = requestParameters['investment'];
        }

        if (requestParameters['name'] != null) {
            queryParameters['name'] = requestParameters['name'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/admin/documents/values/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async adminDocumentsValuesRetrieve(requestParameters: AdminDocumentsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<string>> {
        const response = await this.adminDocumentsValuesRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Can be used to upload documents for use in investor import. Files are uploaded via HTTP multipart, see the [documentation](https://swagger.io/docs/specification/describing-request-body/file-upload/#multipart).  This endpoint is only for investors. They MUST have a valid identification to upload files.
     * Create
     */
    async documentsCreateRaw(requestParameters: DocumentsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Document>> {
        if (requestParameters['file'] == null) {
            throw new runtime.RequiredError(
                'file',
                'Required parameter "file" was null or undefined when calling documentsCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const consumes: runtime.Consume[] = [
            { contentType: 'multipart/form-data' },
        ];
        // @ts-ignore: canConsumeForm may be unused
        const canConsumeForm = runtime.canConsumeForm(consumes);

        let formParams: { append(param: string, value: any): any };
        let useForm = false;
        // use FormData to transmit files using content-type "multipart/form-data"
        useForm = canConsumeForm;
        if (useForm) {
            formParams = new FormData();
        } else {
            formParams = new URLSearchParams();
        }

        if (requestParameters['documentType'] != null) {
            formParams.append('document_type', requestParameters['documentType'] as any);
        }

        if (requestParameters['file'] != null) {
            formParams.append('file', requestParameters['file'] as any);
        }

        const response = await this.request({
            path: `/api/documents/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: formParams,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => DocumentFromJSON(jsonValue));
    }

    /**
     * Can be used to upload documents for use in investor import. Files are uploaded via HTTP multipart, see the [documentation](https://swagger.io/docs/specification/describing-request-body/file-upload/#multipart).  This endpoint is only for investors. They MUST have a valid identification to upload files.
     * Create
     */
    async documentsCreate(requestParameters: DocumentsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Document> {
        const response = await this.documentsCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async documentsDropdownListRaw(requestParameters: DocumentsDropdownListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedDropdownElementList>> {
        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investmentId'] != null) {
            queryParameters['investment_id'] = requestParameters['investmentId'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['name'] != null) {
            queryParameters['name'] = requestParameters['name'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/documents/dropdown/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedDropdownElementListFromJSON(jsonValue));
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async documentsDropdownList(requestParameters: DocumentsDropdownListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedDropdownElementList> {
        const response = await this.documentsDropdownListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * List InvestorProductDocuments of the requesting user.
     */
    async documentsListRaw(requestParameters: DocumentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedDocumentList>> {
        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investmentId'] != null) {
            queryParameters['investment_id'] = requestParameters['investmentId'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['name'] != null) {
            queryParameters['name'] = requestParameters['name'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/documents/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedDocumentListFromJSON(jsonValue));
    }

    /**
     * List InvestorProductDocuments of the requesting user.
     */
    async documentsList(requestParameters: DocumentsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedDocumentList> {
        const response = await this.documentsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async documentsValuesRetrieveRaw(requestParameters: DocumentsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<string>>> {
        if (requestParameters['field'] == null) {
            throw new runtime.RequiredError(
                'field',
                'Required parameter "field" was null or undefined when calling documentsValuesRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['icontains'] != null) {
            queryParameters['icontains'] = requestParameters['icontains'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investmentId'] != null) {
            queryParameters['investment_id'] = requestParameters['investmentId'];
        }

        if (requestParameters['name'] != null) {
            queryParameters['name'] = requestParameters['name'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/documents/values/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async documentsValuesRetrieve(requestParameters: DocumentsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<string>> {
        const response = await this.documentsValuesRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

}

/**
 * @export
 */
export const AdminDocumentsDropdownListFieldEnum = {
    NAME: 'name'
} as const;
export type AdminDocumentsDropdownListFieldEnum = typeof AdminDocumentsDropdownListFieldEnum[keyof typeof AdminDocumentsDropdownListFieldEnum];
/**
 * @export
 */
export const AdminDocumentsDropdownListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    NAME_DESC: '-name',
    NAME_ASC: 'name'
} as const;
export type AdminDocumentsDropdownListOrderingEnum = typeof AdminDocumentsDropdownListOrderingEnum[keyof typeof AdminDocumentsDropdownListOrderingEnum];
/**
 * @export
 */
export const AdminDocumentsListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    NAME_DESC: '-name',
    NAME_ASC: 'name'
} as const;
export type AdminDocumentsListOrderingEnum = typeof AdminDocumentsListOrderingEnum[keyof typeof AdminDocumentsListOrderingEnum];
/**
 * @export
 */
export const AdminDocumentsValuesRetrieveFieldEnum = {
    NAME: 'name'
} as const;
export type AdminDocumentsValuesRetrieveFieldEnum = typeof AdminDocumentsValuesRetrieveFieldEnum[keyof typeof AdminDocumentsValuesRetrieveFieldEnum];
/**
 * @export
 */
export const AdminDocumentsValuesRetrieveOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    NAME_DESC: '-name',
    NAME_ASC: 'name'
} as const;
export type AdminDocumentsValuesRetrieveOrderingEnum = typeof AdminDocumentsValuesRetrieveOrderingEnum[keyof typeof AdminDocumentsValuesRetrieveOrderingEnum];
/**
 * @export
 */
export const DocumentsDropdownListFieldEnum = {
    NAME: 'name'
} as const;
export type DocumentsDropdownListFieldEnum = typeof DocumentsDropdownListFieldEnum[keyof typeof DocumentsDropdownListFieldEnum];
/**
 * @export
 */
export const DocumentsDropdownListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    NAME_DESC: '-name',
    NAME_ASC: 'name'
} as const;
export type DocumentsDropdownListOrderingEnum = typeof DocumentsDropdownListOrderingEnum[keyof typeof DocumentsDropdownListOrderingEnum];
/**
 * @export
 */
export const DocumentsListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    NAME_DESC: '-name',
    NAME_ASC: 'name'
} as const;
export type DocumentsListOrderingEnum = typeof DocumentsListOrderingEnum[keyof typeof DocumentsListOrderingEnum];
/**
 * @export
 */
export const DocumentsValuesRetrieveFieldEnum = {
    NAME: 'name'
} as const;
export type DocumentsValuesRetrieveFieldEnum = typeof DocumentsValuesRetrieveFieldEnum[keyof typeof DocumentsValuesRetrieveFieldEnum];
/**
 * @export
 */
export const DocumentsValuesRetrieveOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    ID_DESC: '-id',
    ID_ASC: 'id',
    NAME_DESC: '-name',
    NAME_ASC: 'name'
} as const;
export type DocumentsValuesRetrieveOrderingEnum = typeof DocumentsValuesRetrieveOrderingEnum[keyof typeof DocumentsValuesRetrieveOrderingEnum];
